import { useState } from 'react'
import { Head, router, usePage } from '@inertiajs/react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from '@/components/ui/Card'
import { Ghost, CheckCircle, AlertCircle } from 'lucide-react'
import appIcon from '@/assets/images/icon-128.png'
import { AuthBackground } from '@/components/auth/AuthBackground'

interface EarlyAccessProps {
  errors?: {
    email?: string[]
  }
}

export default function EarlyAccess() {
  const { props } = usePage<{
    errors?: EarlyAccessProps['errors']
    flash?: { notice?: string; alert?: string }
  }>()
  const { errors, flash } = props

  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    router.post('/early-access', { email }, {
      onFinish: () => {
        setIsSubmitting(false)
        if (!errors?.email) {
          setEmail('')
        }
      },
    })
  }

  return (
    <>
      <Head title="Early Access" />

      <AuthBackground>
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <img src={appIcon} alt="Banshee" className="size-16 mx-auto mb-2 rounded-xl" />
            <div className="inline-block bg-primary/10 text-primary px-4 py-1 rounded-full text-sm font-medium mb-4">
              Coming Soon
            </div>
            <CardTitle className="flex items-center justify-center gap-2">
              <Ghost className="size-6" />
              Summon Early Access
            </CardTitle>
            <CardDescription>
              Banshee.chat is rising soon. Be the first to know when we emerge from the shadows.
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            {flash?.notice && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-primary/10 text-sm text-primary">
                <CheckCircle className="size-4 shrink-0" />
                {flash.notice}
              </div>
            )}

            {flash?.alert && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 text-sm text-destructive">
                <AlertCircle className="size-4 shrink-0" />
                {flash.alert}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="ghost@example.com"
                  required
                />
                {errors?.email && (
                  <p className="text-sm text-destructive">{errors.email[0]}</p>
                )}
              </div>

              <Button type="submit" className="w-full cursor-pointer" disabled={isSubmitting}>
                {isSubmitting ? 'Summoning...' : 'Join the Haunting'}
              </Button>
            </form>
          </CardContent>

          <CardFooter className="flex-col gap-4">
            <p className="text-sm text-muted-foreground">
              Already have an account?
            </p>
            <a
              href="/login"
              className="text-sm text-primary hover:underline cursor-pointer"
            >
              Sign in here
            </a>
          </CardFooter>
        </Card>
      </AuthBackground>
    </>
  )
}
